import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { useUserContext } from 'context/UserContextProvider';
import { differenceInMinutes } from 'date-fns';
import useReopenServiceRecordMutation from 'hooks/mutations/useReopenServiceRecordMutation';
import { forEach, get, isNil } from 'lodash';
import { getI18n, Trans, useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { getLightStatusColor, getMediumStatusColor } from '../helpers/getColourSchemeBasedOnStatus';
import { dropdown, textareaInput } from '../helpers/makeFormFields';
import { LoadingSpinner } from './LoadingSpinner';
import AlertModal from './modals/AlertModal';
import FormEmbeddedAlertModal from './modals/FormEmbeddedAlertModal';
import { StatusTag } from './StatusTag';
import { DropdownOption } from './Table/Filter';

const getTransitions = (statusCodes: any, resourceKey: string, transitions: any) => {
  const transitionOptions: DropdownOption[] = [
    {
      label: getI18n().t('components:update_status_modal.transitions.choose'),
      value: 'unknown',
    },
  ];
  forEach(transitions, (transition) => {
    transitionOptions.push({
      label: getI18n().t(resourceKey + ':status.' + (statusCodes && statusCodes[transition] ? statusCodes[transition] + '.label' : 'unknown')),
      value: String(transition),
    });
  });
  return transitionOptions;
};

const getReasons = (statusCodes: any, reasons: any, resourceKey: string) => {
  const currentReasons = reasons || {};

  const reasonOptions = [
    {
      label: getI18n().t('components:update_status_modal.reasons.choose'),
      value: 'unknown',
    },
  ];

  forEach(Object.keys(currentReasons), (key) => {
    reasonOptions.push({
      label: getI18n().t(
        `${resourceKey}:status.${statusCodes}.${currentReasons && currentReasons[key] ? currentReasons[key] + '.label' : 'unknown'}`,
      ),
      value: key,
    });
  });
  return reasonOptions;
};

export const StatusCard = (props: {
  permission: string;
  serviceRecordStatus?: number;
  canEdit?: boolean;
  completedAt?: string;
  completedTime?: string | null | undefined;
  currentStatus: number | undefined;
  currentStatusReason?: number;
  fileId?: number | undefined;
  id?: number;
  isLoading: boolean;
  isServiceRecord?: boolean;
  // reasons?: { [x: number]: string };
  reasons?: any;
  resourceKey: string;
  shouldHaveFile?: boolean;
  showSubContent?: boolean;
  statusCodes: any;
  subContent?: any;
  subContentOpenByDefault?: boolean;
  statusTransitions?: any;
  hasOpenTask?: boolean;
  updateStatus?: (data: any) => void;
}) => {
  const { t } = useTranslation();
  const {
    permission,
    canEdit = false,
    completedAt,
    completedTime,
    currentStatus,
    currentStatusReason,
    fileId,
    id,
    isLoading,
    isServiceRecord,
    reasons,
    resourceKey,
    shouldHaveFile = false,
    showSubContent = false,
    subContent,
    statusCodes,
    subContentOpenByDefault = false,
    statusTransitions,
    hasOpenTask,
    updateStatus,
  } = props;
  const { isMember, user } = useUserContext();
  const resourceStatusCodes = get(statusCodes, resourceKey);
  const transitions = get(statusTransitions, currentStatus ?? 0, []);
  const defaultIndex = subContentOpenByDefault ? [0] : undefined;

  const userLanguage = t(`location:languages.${user?.language}`);

  const completed4MinAgo = completedTime ? differenceInMinutes(new Date(completedTime), new Date()) > 4 : false;

  const shouldDisplayReopenBttn = currentStatus === 700 || currentStatus === 900;

  yup.addMethod(yup.string, 'completedValidation', function (errorMessage) {
    return this.test(`test-status`, errorMessage, function (value) {
      const { path, createError } = this;
      if (shouldHaveFile) {
        if (value && value == '700') {
          if (isNil(fileId)) {
            return createError({ path, message: errorMessage });
          }
        }
      }
      return true;
    });
  });

  const schema = yup
    .object({
      status: yup.string().max(255).required(),
      message: yup.string().when('status_reason', {
        is: 'clarification_other',
        then: (schema) => schema.min(1).required('Please enter the text').nullable(),
        otherwise: (schema) => schema.nullable(),
      }),
      status_reason: yup.string().max(255).nullable(),
    })
    .required();

  const isAllowed = isMember(permission);

  const reopenServiceRecordMutation = useReopenServiceRecordMutation(id ?? 0);

  const reopenServiceRecord = () => (id ? reopenServiceRecordMutation.mutate() : null);

  const handleUpdateStatus = (data: any) => {
    const payload = {
      ...data,
      status: data.status !== undefined ? Number(data.status) : undefined,
      status_reason: data.status_reason !== null ? Number(data.status_reason) : undefined,
    };

    updateStatus?.(payload);
  };

  return (
    <Stack
      direction={'column'}
      w={'100%'}
      bgColor={getLightStatusColor(
        statusCodes && Object.hasOwn(statusCodes, currentStatus ?? 0) ? resourceStatusCodes?.[currentStatus ?? 0] : 'unknown',
      )}
      p={6}
      borderRadius="lg"
      boxShadow={useColorModeValue('md', 'md-dark')}
      pos="relative"
      data-test-id="status-card"
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Accordion allowToggle defaultIndex={defaultIndex}>
          <AccordionItem border="none">
            <HStack justifyContent="space-between">
              <StatusTag
                completedAt={completedAt}
                resourceKey={resourceKey}
                currentStatus={currentStatus ?? 0}
                currentStatusReason={currentStatusReason}
              />
              <Flex>
                {transitions.length > 0 && canEdit && (
                  <Button
                    disabled={!isAllowed || (isServiceRecord && currentStatus === 500)}
                    variant="primary"
                    data-test-id="status-update-button"
                    onClick={() => {
                      NiceModal.show(FormEmbeddedAlertModal as any, {
                        content: {
                          header: t('components:update_status_modal.header'),
                          formTestId: 'status-form',
                          formFields: (props: { control: any; register: any; errors: any; watch: any; watchedFields: { status: number } }) => {
                            const { control, errors, watchedFields, watch, register } = props;
                            const selectedStatus = watchedFields?.status;
                            const reasonOptions = getReasons(statusCodes[selectedStatus], reasons?.[selectedStatus], resourceKey);
                            return (
                              <>
                                {dropdown({
                                  name: 'status',
                                  label: t('components:update_status_modal.status.label'),
                                  // @ts-ignore
                                  control,
                                  errors,
                                  options: getTransitions(statusCodes, resourceKey, transitions),
                                })}
                                {reasonOptions.length > 1 && (
                                  <Box mt={5}>
                                    {dropdown({
                                      name: 'status_reason',
                                      label: t('components:update_status_modal.status_reason.label'),
                                      // @ts-ignore
                                      control,
                                      errors,
                                      options: reasonOptions,
                                    })}
                                  </Box>
                                )}
                                {watch('status_reason') === '590' && (
                                  <Box mt={5}>
                                    {textareaInput({
                                      name: `message`,
                                      placeholder: t('service_record:status.clarification_other.placeholder'),
                                      label: (
                                        <>
                                          <Text mb={2}>
                                            <Trans i18nKey={'service_record:status.clarification_other.message'} />
                                          </Text>
                                          <Text
                                            fontSize={'xs'}
                                            opacity={0.5}
                                            dangerouslySetInnerHTML={{
                                              __html: t(`service_record:status.clarification_other.subtitleTextLang`, {
                                                lang: userLanguage,
                                              }).replace(/&nbsp;/g, ' '),
                                            }}
                                          ></Text>
                                        </>
                                      ),
                                      register,
                                      errors,
                                      required: watch('status_reason') === '590',
                                    })}
                                  </Box>
                                )}
                              </>
                            );
                          },
                          footer: {
                            buttons: {
                              formButtonTestId: 'scan-submit-button',
                              actionCaption: t('common:edit'),
                            },
                          },
                        },
                        onMutate: handleUpdateStatus,
                        schema,
                      });
                    }}
                  >
                    {t('common:edit')}
                  </Button>
                )}

                {isServiceRecord && shouldDisplayReopenBttn && !hasOpenTask && (
                  <Button
                    variant="primary"
                    disabled={isLoading || !isAllowed}
                    data-test-id={`assign`}
                    onClick={() => {
                      NiceModal.show(AlertModal as any, {
                        onSubmit: reopenServiceRecord,
                        children: completed4MinAgo ? (
                          <Trans i18nKey="pages:service_record.reopen.completed_message" />
                        ) : (
                          <Trans i18nKey="pages:service_record.reopen.canceled_message" />
                        ),
                        content: {
                          header: t('pages:service_record.reopen.header'),
                          footer: {
                            buttons: {
                              cancelCaption: t('common:cancel'),
                              actionCaption: t('pages:service_record.reopen.label'),
                            },
                          },
                        },
                      });
                    }}
                  >
                    {t('pages:service_record.reopen.label')}
                  </Button>
                )}

                {showSubContent && (
                  <AccordionButton w={'fit-content'}>
                    <AccordionIcon />
                  </AccordionButton>
                )}
              </Flex>
            </HStack>

            {subContent ? (
              <AccordionPanel pb={4}>
                <Divider
                  borderWidth="1px"
                  borderColor={getMediumStatusColor(
                    statusCodes && Object.hasOwn(statusCodes, currentStatus ?? 0) ? resourceStatusCodes?.[currentStatus ?? 0] : 'unknown',
                  )}
                  mb={2}
                />
                <Box paddingInline={2} paddingBlock={4}>
                  {subContent}
                </Box>
              </AccordionPanel>
            ) : null}
          </AccordionItem>
        </Accordion>
      )}
    </Stack>
  );
};
